import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Clock, CheckCircle2, Upload, LogOut, Building2, Loader2 } from "lucide-react";

/**
 * SEC-003 · Pantalla para usuarios con `suscripcion_status=pendiente_aprobacion`.
 * No accede al ERP; solo consulta el estado de la solicitud + permite subir/reemplazar comprobante.
 */
export default function EsperandoAprobacion() {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const { logout } = useAuth() || {};
  const navigate = useNavigate();

  const cargar = async () => {
    try {
      const { data } = await axios.get(`${API}/suscripciones/mi-estado`);
      setEstado(data);
      // Si ya fue aprobada, redirigir al panel
      if (data?.suscripcion?.estatus === "activa" || data?.usuario?.estado_cuenta === "active") {
        navigate("/panel");
      }
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/login"); return; }
      toast.error("Error al consultar estado: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); const t = setInterval(cargar, 30000); return () => clearInterval(t); }, []);

  const subirComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!estado?.suscripcion?.suscripcion_id) {
      toast.error("Sin suscripcion pendiente asociada");
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Ticket #228412 · Esta pantalla es para usuarios CON sesión (mi-estado):
      // usar el endpoint autenticado; el público exige ?token= firmado y aquí no existe.
      await axios.post(`${API}/suscripciones/${estado.suscripcion.suscripcion_id}/comprobante`, fd);
      toast.success("Comprobante enviado. Un administrador lo revisara.");
      cargar();
    } catch (err) {
      if (err?.response?.status === 401) { navigate("/login"); return; }
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSubiendo(false);
    }
  };

  const salir = async () => {
    if (logout) await logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4" data-testid="esperando-aprobacion">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 border border-stone-200">
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-amber-50 mb-4">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Tu cuenta esta en revision</h1>
        <p className="text-sm text-stone-500 mb-6">
          Hemos recibido tu solicitud. Un administrador aprobara tu cuenta despues de verificar el comprobante de pago.
        </p>

        {estado?.empresa && (
          <div className="mb-4 p-4 bg-stone-50 rounded-xl">
            <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Empresa</p>
            <p className="font-semibold flex items-center gap-2 text-stone-700">
              <Building2 className="w-4 h-4" /> {estado.empresa.nombre}
            </p>
            {estado.empresa.estatus && (
              <p className="text-xs text-stone-500 mt-1">Estado: <span className="font-medium">{estado.empresa.estatus}</span></p>
            )}
          </div>
        )}

        {estado?.suscripcion && (
          <div className="mb-4 p-4 bg-stone-50 rounded-xl">
            <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Suscripcion</p>
            <p className="font-medium text-stone-700">Plan: {estado.suscripcion.plan_id || "—"}</p>
            <p className="text-xs text-stone-500 mt-1">Estatus: {estado.suscripcion.estatus || "pendiente"}</p>
            {estado.suscripcion.comprobante_url ? (
              <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Comprobante recibido
              </p>
            ) : (
              <p className="text-xs text-amber-600 mt-2">Sin comprobante aun. Subelo abajo.</p>
            )}
          </div>
        )}

        <label className="block mb-3">
          <span className="text-sm font-medium text-stone-700 mb-2 block">
            {estado?.suscripcion?.comprobante_url ? "Reemplazar comprobante" : "Subir comprobante de pago"}
          </span>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              disabled={subiendo}
              onChange={subirComprobante}
              className="text-sm"
              data-testid="input-comprobante"
            />
            {subiendo && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
          <p className="text-xs text-stone-400 mt-1">Formatos: JPG, PNG, PDF · Max 5 MB</p>
        </label>

        <div className="border-t border-stone-200 pt-4 mt-4 flex items-center justify-between">
          <button
            onClick={cargar}
            className="text-sm text-rose-600 hover:text-rose-700"
            data-testid="btn-refrescar"
          >
            Refrescar estado
          </button>
          <button
            onClick={salir}
            className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
            data-testid="btn-salir"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesion
          </button>
        </div>
      </div>
    </div>
  );
}
